import axios from "axios";
import { API_BASE_URL } from "../config";

const API_URL = API_BASE_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    // If response is successful, just return it
    return response;
  },
  (error) => {
    // Check if error is due to invalid/expired token
    if (error.response && error.response.status === 401) {
      const errorDetail = error.response.data?.detail || "";
      
      // Check if it's a credentials validation error (token expired/invalid)
      if (errorDetail.includes("Could not validate credentials") || 
          errorDetail.includes("Invalid token") ||
          errorDetail.includes("Token expired")) {
        
        // Clear stored auth data
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        
        // Redirect to login page
        window.location.href = "/login";
        
        // Return a rejected promise with a user-friendly message
        return Promise.reject(new Error("Your session has expired. Please log in again."));
      }
    }
    
    // For other errors, just pass them through
    return Promise.reject(error);
  }
);

/**
 * Register a new user (partner or agency)
 * @param {FormData} userData - User registration data as FormData (for file uploads)
 */
export async function registerUser(userData) {
  // If userData is FormData, use multipart/form-data
  const isFormData = userData instanceof FormData;
  
  const config = isFormData ? {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  } : {};
  
  const response = await api.post("/users/register", userData, config);
  return response.data;
}

/**
 * Login user
 * @param {string} email - User's email
 * @param {string} password - User's password
 */
export async function loginUser(email, password) {
  const response = await api.post("/users/login", { email, password });
  const { access_token, user } = response.data;
  
  // Store token in localStorage
  localStorage.setItem("token", access_token);
  
  // Only store user if it exists and is valid
  if (user && typeof user === 'object') {
    localStorage.setItem("user", JSON.stringify(user));
  }
  
  return response.data;
}

/**
 * Logout user
 */
export function logoutUser() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

/**
 * Clear invalid stored data (cleanup utility)
 */
export function cleanupInvalidData() {
  try {
    const userStr = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    
    // Check if user data is invalid
    if (userStr && (userStr === "undefined" || userStr === "null")) {
      localStorage.removeItem("user");
    }
    
    // Try to parse user data, remove if invalid
    if (userStr && userStr !== "undefined" && userStr !== "null") {
      try {
        JSON.parse(userStr);
      } catch {
        localStorage.removeItem("user");
      }
    }
    
    // If there's no token but there's user data, clear user data
    if (!token && userStr) {
      localStorage.removeItem("user");
    }
  } catch (error) {
    console.error("Error during cleanup:", error);
  }
}

/**
 * Get current user profile
 */
export async function getCurrentUser() {
  const response = await api.get("/users/me");
  return response.data;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!localStorage.getItem("token");
}

/**
 * Get stored user data
 */
export function getStoredUser() {
  try {
    const userStr = localStorage.getItem("user");
    
    // Check if userStr is null, undefined, or the string "undefined"
    if (!userStr || userStr === "undefined" || userStr === "null") {
      return null;
    }
    
    return JSON.parse(userStr);
  } catch (error) {
    console.error("Error parsing stored user data:", error);
    // Clear invalid data
    localStorage.removeItem("user");
    return null;
  }
}

export { api };